import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCurrentAccount,
  getIsFullAccessUser,
  getIsPrimaryUser,
  getIsReadOnlyUser,
  selectIsEquityIRAAccount,
} from '../../store/selectors/accountSelectors';
import { accountTypes, accountTypeKeys } from '../enum/api/accountTypes';
import { parseEnumType } from '../helpers/enumMappers';

// Guards the "Buy at Target Price" (trigger order) create entry points. Two
// independent axes gate it:
//
// User permission level (74317 / 74319) — checked first, so a non-primary
// user is blocked regardless of the account's own eligibility:
//   Read Only    → CTA rendered locked; tapping shows the Read Only Access modal
//   Full Access  → non-primary; tapping shows the Primary Account Holder Only modal
//   Primary      → allowed (falls through to account eligibility below)
//
// Account eligibility (71992 acceptance criteria):
//   S1 delivery-only → CTA visible, blocked with a verify-account popup on click
//   S2 pending       → CTA visible, blocked with a call-us popup on click
//   S3 Equity IRA    → CTA hidden entirely (cash is held at Equity)
// S1/S2 are mutually exclusive: delivery-only is keyed off AccountType (Basic),
// not the ALLOW_BASIC_ACCOUNT_CHANGES permission — that permission can also be
// granted to plain pending Individual accounts, so using it as the S1 signal
// misclassified pending accounts as delivery-only. "Pending" covers AccountStatus
// Pending (1), RequiresAttention (4), and Err / Enhanced Review Required (7) —
// same grouping used by PriceAlertFlow.js, BuyFlow.js, and TwoFACard.js for
// account-eligibility gating.
//
// Usage:
//   - Gate CTA visibility with `!isTriggerOrderEntryHidden` (hides Equity IRA).
//   - Render the CTA in a locked style when `isReadOnly`.
//   - Call blockIfIneligible() at the top of the open handler; if it returns
//     true a block modal is showing, so return early instead of opening the flow.
//   - Render <TriggerOrderBlockModal variant={verificationVariant} .../>.
const useTriggerOrderVerification = () => {
  const account = useSelector(getCurrentAccount);
  const isEquityIRA = useSelector(selectIsEquityIRAAccount);
  const isReadOnly = useSelector(getIsReadOnlyUser);
  const isFullAccess = useSelector(getIsFullAccessUser);
  const isPrimary = useSelector(getIsPrimaryUser);
  const [verificationVariant, setVerificationVariant] = useState(null);

  const isDeliveryOnlyAccount =
    parseEnumType(accountTypeKeys, account?.AccountType) === accountTypes.Basic;

  const isPendingAccount =
    account?.AccountStatus === 1 ||
    account?.AccountStatus === 4 ||
    account?.AccountStatus === 7;

  const getBlockingVariant = () => {
    // Permission level first: a non-primary user can never create/place.
    if (isReadOnly) return 'readOnly';
    if (isFullAccess && !isPrimary) return 'primaryOnly';

    // Account eligibility (primary users, or roles not covered above).
    if (isDeliveryOnlyAccount) return 'deliveryOnly';
    if (isPendingAccount) return 'pending';
    return null;
  };

  const blockIfIneligible = () => {
    const variant = getBlockingVariant();
    if (variant) {
      setVerificationVariant(variant);
      return true;
    }
    return false;
  };

  return {
    // Equity IRA never sees the entry point.
    isTriggerOrderEntryHidden: isEquityIRA,
    // Read Only users see a locked CTA (blocked with a popup on tap).
    isReadOnly,
    verificationVariant,
    closeVerification: () => setVerificationVariant(null),
    blockIfIneligible,
  };
};

export default useTriggerOrderVerification;
